import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
    Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import './AiMessageContent.css';

/** Brand-consistent palette for AI charts (same family as the Financeiro donuts). */
const PALETTE = ['#7266e8', '#b15fd8', '#4fb286', '#f2a541', '#e8656f', '#5b9bd5', '#8a86a0'];

interface ChartSpec {
    type: 'bar' | 'line' | 'pie';
    title?: string;
    labels: string[];
    values: number[];
}

/**
 * Renders one assistant message: Markdown (GFM tables, bold, lists — HTML is NOT
 * rendered, so model/customer text can't inject markup) plus our chart blocks:
 * a fenced ```chart code block containing {"type","title","labels","values"}
 * becomes a real recharts visualization — the AI's "artifact".
 */
export default function AiMessageContent({ content }: { content: string }) {
    const segments = splitChartBlocks(content);
    return (
        <div className="ai-md">
            {segments.map((seg, i) =>
                seg.chart ? (
                    <AiChart key={i} spec={seg.chart} />
                ) : (
                    <ReactMarkdown key={i} remarkPlugins={[remarkGfm]}>{seg.text!}</ReactMarkdown>
                ),
            )}
        </div>
    );
}

interface Segment { text?: string; chart?: ChartSpec; }

/** Split out ```chart fenced blocks; invalid JSON stays as plain text. */
function splitChartBlocks(content: string): Segment[] {
    const segments: Segment[] = [];
    const re = /```chart\s*\n([\s\S]*?)```/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
        if (m.index > last) segments.push({ text: content.slice(last, m.index) });
        const spec = parseChart(m[1]);
        if (spec) segments.push({ chart: spec });
        else segments.push({ text: '```\n' + m[1] + '```' });
        last = m.index + m[0].length;
    }
    if (last < content.length) segments.push({ text: content.slice(last) });
    return segments.length ? segments : [{ text: content }];
}

function parseChart(raw: string): ChartSpec | null {
    try {
        const o = JSON.parse(raw);
        const okType = o.type === 'bar' || o.type === 'line' || o.type === 'pie';
        const okData = Array.isArray(o.labels) && Array.isArray(o.values)
            && o.labels.length > 0 && o.labels.length === o.values.length
            && o.values.every((v: unknown) => typeof v === 'number' && isFinite(v as number));
        if (!okType || !okData) return null;
        return {
            type: o.type,
            title: typeof o.title === 'string' ? o.title : undefined,
            labels: o.labels.map(String).slice(0, 30),
            values: (o.values as number[]).slice(0, 30),
        };
    } catch {
        return null;
    }
}

function AiChart({ spec }: { spec: ChartSpec }) {
    const data = spec.labels.map((label, i) => ({ name: label, valor: spec.values[i] }));
    return (
        <div className="ai-chart" data-cy="ai-chart">
            {spec.title && <div className="ai-chart__title">{spec.title}</div>}
            <ResponsiveContainer width="100%" height={260}>
                {spec.type === 'pie' ? (
                    <PieChart>
                        <Pie data={data} dataKey="valor" nameKey="name" innerRadius={55} outerRadius={90}
                             paddingAngle={2} strokeWidth={0} label>
                            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                ) : spec.type === 'line' ? (
                    <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eceaf6" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={36} />
                        <Tooltip />
                        <Line type="monotone" dataKey="valor" stroke={PALETTE[0]} strokeWidth={2.5}
                              dot={{ r: 4, fill: PALETTE[0] }} />
                    </LineChart>
                ) : (
                    <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eceaf6" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={36} />
                        <Tooltip cursor={{ fill: 'rgba(114,102,232,0.08)' }} />
                        <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                    </BarChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}
